export type SoundCue = 'place' | 'remove' | 'button' | 'lever' | 'click';
export interface SoundPreferences { enabled: boolean; volume: number }

const tones: Record<SoundCue, [number, number, number]> = {
  place: [600, 900, 0.045], remove: [300, 120, 0.06],
  button: [800, 500, 0.04], lever: [450, 700, 0.055], click: [1000, 850, 0.025],
};

/** Short synthesized cues; no downloads or work on the simulation tick path. */
export class SoundFeedback {
  private context?: AudioContext;
  private lastPlayed = -Infinity;
  constructor(private preferences: () => SoundPreferences) {}

  play(cue: SoundCue): void {
    const { enabled, volume } = this.preferences();
    if (!enabled || !Number.isFinite(volume) || volume <= 0) return;
    const now = performance.now();
    // Bound audio allocations during drag placement and repeated UI input.
    if (now - this.lastPlayed < 40) return;
    this.lastPlayed = now;
    try {
      if (typeof AudioContext === 'undefined') return;
      const context = this.context ??= new AudioContext();
      const start = () => {
        const current = this.preferences();
        if (!current.enabled || current.volume <= 0 || context.state !== 'running') return;
        const [from, to, duration] = tones[cue];
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const time = context.currentTime;
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(from, time);
        oscillator.frequency.exponentialRampToValueAtTime(to, time + duration);
        gain.gain.setValueAtTime(0.0001, time);
        gain.gain.exponentialRampToValueAtTime(Math.min(1, current.volume) * 0.12 + 0.0001, time + 0.004);
        gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.onended = () => { oscillator.disconnect(); gain.disconnect(); };
        oscillator.start(time);
        oscillator.stop(time + duration);
      };
      if (context.state === 'suspended') void context.resume().then(start).catch(() => {});
      else start();
    } catch {
      // Audio availability never prevents an editor action.
    }
  }
}
